import * as Notifications from "expo-notifications";
import { useEffect } from "react";
import { Platform } from "react-native";

import { useAuth } from "@/lib/auth";
import { log } from "@/logger";

import { navigateFromNotificationData } from "./navigateFromNotificationData";
import { registerPushDevice } from "./registerPushDevice";

// Foreground only: without this, Expo hides incoming pushes while the app is open.
// Background / killed delivery still follows the user's system notification settings.
Notifications.setNotificationHandler({
    handleNotification: async () => ({
        shouldShowBanner: true,
        shouldShowList: true,
        shouldPlaySound: false,
        shouldSetBadge: false,
    }),
});

function asRecord(value: unknown): Record<string, unknown> | undefined {
    if (value && typeof value === "object" && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }
    return undefined;
}

/**
 * Android: custom keys are on `content.data` (and `trigger.remoteMessage.data`).
 * iOS: `content.data` is null; keys are on the APNs `userInfo` (`trigger.payload`).
 */
function getNotificationDataFromResponse(
    response: Notifications.NotificationResponse
): Record<string, unknown> | undefined {
    log.debug("getNotificationDataFromResponse", response);
    const { content, trigger } = response.notification.request;
    const pushTrigger =
        trigger &&
        typeof trigger === "object" &&
        "type" in trigger &&
        trigger.type === "push"
            ? (trigger as Notifications.PushNotificationTrigger)
            : undefined;

    return (
        asRecord(content.data) ??
        asRecord(pushTrigger?.payload) ??
        asRecord(pushTrigger?.remoteMessage?.data)
    );
}

/**
 * Registers the push device after auth and handles notification taps (deep links).
 */
export function PushNotificationsBootstrap() {
    const { isAuthenticated, isLoading } = useAuth();

    useEffect(() => {
        if (Platform.OS === "web" || isLoading || !isAuthenticated) {
            return;
        }
        log.debug("Registering push device");
        void registerPushDevice();
    }, [isAuthenticated, isLoading]);

    useEffect(() => {
        if (Platform.OS === "web") {
            return;
        }

        /**
         * Handles notification taps (deep links) when the app is running or in the foreground.
         */
        const subscription =
            Notifications.addNotificationResponseReceivedListener(
                (response) => {
                    try {
                        navigateFromNotificationData(
                            getNotificationDataFromResponse(response)
                        );
                    } catch (error) {
                        log.warn(
                            "Failed to handle notification response",
                            error
                        );
                    }
                }
            );

        /**
         * Handles notification taps (deep links) on app cold start.
         */
        void Notifications.getLastNotificationResponseAsync().then(
            (response) => {
                if (!response) {
                    return;
                }
                navigateFromNotificationData(
                    getNotificationDataFromResponse(response)
                );
            }
        );

        return () => subscription.remove();
    }, []);

    return null;
}
